/**
 * LegalModal - 用户协议 / 隐私政策查看弹窗
 * 从 public/legal/ 加载 .md 文件并渲染
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FileText, Shield, ChevronRight } from 'lucide-react';

// 简易 Markdown 渲染（无需额外依赖）
const renderMarkdown = (md) => {
    if (!md) return [];
    const lines = md.split('\n');
    const elements = [];
    let key = 0;

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        if (line.startsWith('# ')) {
            elements.push(
                <h1 key={key++} className="text-xl font-black text-gray-900 mb-4 pb-3 border-b border-gray-100">
                    {line.slice(2)}
                </h1>
            );
        } else if (line.startsWith('## ')) {
            elements.push(
                <h2 key={key++} className="text-base font-bold text-gray-800 mt-6 mb-2">
                    {line.slice(3)}
                </h2>
            );
        } else if (line.startsWith('### ')) {
            elements.push(
                <h3 key={key++} className="text-sm font-semibold text-gray-700 mt-4 mb-1">
                    {line.slice(4)}
                </h3>
            );
        } else if (line.startsWith('---')) {
            elements.push(<hr key={key++} className="border-gray-100 my-4" />);
        } else if (line.startsWith('- ')) {
            elements.push(
                <li key={key++} className="text-sm text-gray-600 ml-4 mb-1 list-disc leading-relaxed">
                    {line.slice(2).replace(/\*\*(.*?)\*\*/g, '$1')}
                </li>
            );
        } else if (/^\d+\./.test(line)) {
            elements.push(
                <li key={key++} className="text-sm text-gray-600 ml-4 mb-1 list-decimal leading-relaxed">
                    {line.replace(/^\d+\.\s*/, '').replace(/\*\*(.*?)\*\*/g, '$1')}
                </li>
            );
        } else if (line.startsWith('| ')) {
            // 跳过表格分隔行
            if (line.includes('---')) continue;
            const cells = line.split('|').filter(c => c.trim());
            const isHeader = lines[i + 1]?.includes('---');
            elements.push(
                <tr key={key++} className={isHeader ? 'bg-gray-50' : 'border-t border-gray-50'}>
                    {cells.map((cell, ci) => (
                        isHeader
                            ? <th key={ci} className="px-3 py-2 text-xs font-bold text-left text-gray-700">{cell.trim()}</th>
                            : <td key={ci} className="px-3 py-2 text-xs text-gray-600">{cell.trim().replace(/\*\*(.*?)\*\*/g, '$1')}</td>
                    ))}
                </tr>
            );
        } else if (line.trim() === '') {
            elements.push(<div key={key++} className="h-2" />);
        } else {
            // 普通段落，处理粗体
            const parts = line.split(/\*\*(.*?)\*\*/g);
            elements.push(
                <p key={key++} className="text-sm text-gray-600 leading-relaxed mb-1">
                    {parts.map((part, pi) =>
                        pi % 2 === 1
                            ? <strong key={pi} className="font-semibold text-gray-800">{part}</strong>
                            : part
                    )}
                </p>
            );
        }
    }

    // 包裹表格行
    const wrapped = [];
    let tableRows = [];
    let inTable = false;

    for (const el of elements) {
        if (el.type === 'tr') {
            inTable = true;
            tableRows.push(el);
        } else {
            if (inTable) {
                wrapped.push(
                    <div key={`table-${wrapped.length}`} className="overflow-x-auto rounded-lg border border-gray-100 my-3">
                        <table className="w-full">{tableRows}</table>
                    </div>
                );
                tableRows = [];
                inTable = false;
            }
            wrapped.push(el);
        }
    }
    if (inTable) {
        wrapped.push(
            <div key="table-last" className="overflow-x-auto rounded-lg border border-gray-100 my-3">
                <table className="w-full">{tableRows}</table>
            </div>
        );
    }

    return wrapped;
};

const DOCS = {
    terms: {
        title: '用户服务协议',
        icon: FileText,
        color: 'blue',
        file: '/legal/用户协议.md',
    },
    privacy: {
        title: '隐私政策',
        icon: Shield,
        color: 'purple',
        file: '/legal/隐私政策.md',
    },
};

const colorMap = {
    blue: {
        bg: 'bg-blue-50',
        text: 'text-blue-600',
        badge: 'bg-gradient-to-r from-blue-500 to-indigo-600',
        tab: 'bg-blue-500 text-white',
    },
    purple: {
        bg: 'bg-purple-50',
        text: 'text-purple-600',
        badge: 'bg-gradient-to-r from-purple-500 to-pink-600',
        tab: 'bg-purple-500 text-white',
    },
};

const LegalModal = ({ isOpen, onClose, defaultDoc = 'terms' }) => {
    const [activeDoc, setActiveDoc] = useState(defaultDoc);
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setActiveDoc(defaultDoc);
    }, [isOpen, defaultDoc]);

    useEffect(() => {
        if (!isOpen) return;
        const doc = DOCS[activeDoc];
        if (!doc) return;

        setLoading(true);
        setContent('');

        fetch(doc.file)
            .then(res => {
                if (!res.ok) throw new Error('文件加载失败');
                return res.text();
            })
            .then(text => setContent(text))
            .catch(() => setContent('# 内容加载失败\n\n请稍后重试或联系客服。'))
            .finally(() => setLoading(false));
    }, [isOpen, activeDoc]);

    const doc = DOCS[activeDoc];
    const colors = colorMap[doc?.color || 'blue'];

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* 遮罩 */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm z-[60]"
                    />

                    {/* 弹窗 */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 350 }}
                        className="fixed inset-0 flex items-center justify-center z-[60] p-4 pointer-events-none"
                    >
                        <div className="relative w-full max-w-[600px] max-h-[85vh] bg-white rounded-3xl shadow-2xl overflow-hidden pointer-events-auto flex flex-col">

                            {/* 背景装饰 */}
                            <div className="absolute -top-16 -right-16 w-32 h-32 bg-blue-100 rounded-full blur-3xl opacity-50 pointer-events-none" />
                            <div className="absolute -bottom-16 -left-16 w-32 h-32 bg-purple-100 rounded-full blur-3xl opacity-50 pointer-events-none" />

                            {/* 头部 */}
                            <div className="relative px-6 pt-5 pb-4 border-b border-gray-50 flex-shrink-0">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-9 h-9 rounded-xl ${colors.badge} flex items-center justify-center shadow-md`}>
                                            {React.createElement(doc.icon, { className: 'w-4 h-4 text-white' })}
                                        </div>
                                        <div>
                                            <h2 className="text-base font-bold text-gray-900">{doc.title}</h2>
                                            <p className="text-[10px] text-gray-400">AI门道 · 请仔细阅读</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={onClose}
                                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>

                                {/* Tab 切换 */}
                                <div className="flex gap-2 mt-4">
                                    {Object.entries(DOCS).map(([key, d]) => {
                                        const isActive = key === activeDoc;
                                        const c = colorMap[d.color];
                                        return (
                                            <button
                                                key={key}
                                                onClick={() => setActiveDoc(key)}
                                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                                    isActive
                                                        ? `${c.tab} shadow-sm`
                                                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                                }`}
                                            >
                                                {React.createElement(d.icon, { className: 'w-3 h-3' })}
                                                {d.title}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* 内容区域 - 可滚动 */}
                            <div className="relative flex-1 overflow-y-auto px-6 py-5 min-h-0">
                                {loading ? (
                                    <div className="flex flex-col items-center justify-center py-20 gap-3">
                                        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                        <p className="text-sm text-gray-400">加载中...</p>
                                    </div>
                                ) : (
                                    <div className="prose prose-sm max-w-none">
                                        {renderMarkdown(content)}
                                    </div>
                                )}
                            </div>

                            {/* 底部确认按钮 */}
                            <div className="px-6 py-4 border-t border-gray-50 flex-shrink-0 bg-white">
                                <button
                                    onClick={onClose}
                                    className={`w-full py-2.5 rounded-xl ${colors.badge} text-white text-sm font-bold shadow-md hover:opacity-90 transition-opacity`}
                                >
                                    我已知晓
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default LegalModal;
